import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Inject, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';

import { EventMediaTracking } from '@frontend/sports/betting-offer/feature/media';
import { EventDetailsColumnType } from '@frontend/sports/betting-offer/feature/model';
import { getAnimationIconForSport, getStatsIcon, getVideoIcon } from '@frontend/sports/common/core/feature/icon';
import { GRID_CONFIG, GridConfig } from '@frontend/sports/grid/core/feature/config';
import { EventMediaService } from '@frontend/sports/grid/core/feature/event-media';
import { GridEvent, GridMedia } from '@frontend/sports/grid/core/feature/model';
import { TrackingSource, trackingConstants } from '@frontend/sports/tracking/feature';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { EventMediaTrackingService } from './event-media-tracking.service';

@Component({
    selector: 'ms-event-media',
    templateUrl: 'event-media.component.html',
    styleUrls: ['./event-media.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [EventMediaTrackingService],
    imports: [NgClass, IconCustomComponent, DsButtonIcon],
})
export class EventMediaComponent implements OnInit, OnDestroy {
    @Input() event: GridEvent;
    @Input() media: GridMedia;
    @Input() tracking: TrackingSource;
    @Input() betBuilderEpcotEnabled = false;
    @Output() statsClicked = new EventEmitter<void>();
    @HostBinding('class') className = 'grid-media';
    isStatsHovered: boolean;
    isVideoHovered: boolean;
    isAnimationHovered: boolean;
    mediaType = EventDetailsColumnType;
    previousMedia: GridMedia;
    private hoveredIcon?: EventDetailsColumnType;

    constructor(
        private eventMediaService: EventMediaService,
        @Optional() @Inject(GRID_CONFIG) private gridConfig: GridConfig | null,
    ) {}

    @HostBinding('class.hidden') get isHidden(): boolean {
        return !!this.gridConfig && !this.gridConfig.areEmptyEventStatisticsVisible && !(this.hasAnimation || this.hasStats || this.hasVideo);
    }

    ngOnInit(): void {
        this.previousMedia = this.media;
        this.media = { animationEvent: '', enabled: true, statsEvent: '', videoEvent: '' };
    }

    ngOnDestroy(): void {
        this.media = this.previousMedia;
    }

    get hasVideo(): boolean {
        return this.eventMediaService.hasVideo(this.event);
    }

    get hasAnimation(): boolean {
        return this.eventMediaService.hasAnimation(this.event);
    }

    get hasStats(): boolean {
        return this.eventMediaService.hasStats(this.event);
    }

    get statsActive(): boolean {
        return this.eventMediaService.statsActive(this.event, this.media);
    }

    get videoActive(): boolean {
        return this.eventMediaService.videoActive(this.event, this.media);
    }

    get animationActive(): boolean {
        return this.eventMediaService.animationActive(this.event, this.media);
    }

    get animationIconClassHover(): string {
        return getAnimationIconForSport(this.event.sport.id, true);
    }

    get videoIconClassHover(): string {
        return getVideoIcon(true);
    }

    get animationIconClass(): string {
        const isActive = this.hoveredIcon === EventDetailsColumnType.Animation || this.media.animationEvent === this.event.id;

        return getAnimationIconForSport(this.event.sport.id, isActive);
    }

    get videoIconClass(): string {
        return getVideoIcon(this.hoveredIcon === EventDetailsColumnType.Video || this.media.videoEvent === this.event.id);
    }

    get statsIconClass(): string {
        const isActive = this.hoveredIcon === EventDetailsColumnType.Stats || this.media.statsEvent === this.event.id;

        return this.eventMediaService.getStatsIcon(isActive);
    }

    get statsIconClassHover(): string {
        return getStatsIcon(true);
    }

    get statsIconPadding(): boolean {
        return !(this.hasAnimation || this.hasVideo) ? true : false;
    }

    get mediaTracking(): EventMediaTracking {
        return {
            name: this.event.name,
            additional: {
                [trackingConstants.SPORT_ID]: this.event.sport.id.toString(),
                [trackingConstants.SPORT_EVENT_ID]: this.event.id.toString(),
            },
            ...this.tracking,
        };
    }

    toggleHover(eventId: number, hovered: boolean) {
        switch (eventId) {
            case EventDetailsColumnType.Stats:
                this.isStatsHovered = hovered;
                break;
            case EventDetailsColumnType.Video:
                this.isVideoHovered = hovered;
                break;
            default:
                this.isAnimationHovered = hovered;
        }
    }

    activateMedia(event: MouseEvent, activeType: EventDetailsColumnType): void {
        if (this.eventMediaService.statsActive(this.event, this.media) && activeType === EventDetailsColumnType.Stats) {
            const isBetStationMedia = this.eventMediaService.activateMedia(activeType, this.event, this.media, true, true);
            this.eventMediaService.trackChanges(activeType, this.event, this.mediaTracking, true);
            if (isBetStationMedia) {
                this.previousMedia = this.media;
                this.media = { animationEvent: '', enabled: true, statsEvent: '', videoEvent: '' };
            }
        } else {
            if (this.previousMedia?.statsEvent === this.event.id) {
                this.media = this.previousMedia;
            }
            this.eventMediaService.activateMedia(activeType, this.event, this.media, false, true);
            this.eventMediaService.trackChanges(activeType, this.event, this.mediaTracking);
        }
        this.statsClicked.emit();
        event.preventDefault();
        event.stopImmediatePropagation();
    }
}
